import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import type { AlertItem } from '../components/monitoring/AlertFeed';

/**
 * G19 telemetry bridge — the live alert feed. Detections flow
 * `forge_eventbridge.py` → `attack.detected` → monitor Lambda → telemetry table
 * → these read endpoints. The monitor handler returns the AlertItem shape the
 * `AlertFeed` component already renders.
 *
 * Polls every 10s so the feed stays live without a WebSocket (the backlog
 * marks WebSocket streaming as a later pass).
 */

interface AlertsResponse {
  items?: ReadonlyArray<AlertItem>;
  count?: number;
}

const REFETCH_MS = 10_000;

/** Alerts for a single range, newest-first. */
export function useRangeAlerts(rangeId: string | undefined) {
  return useQuery<ReadonlyArray<AlertItem>>({
    queryKey: ['alerts', 'range', rangeId],
    enabled: Boolean(rangeId),
    refetchInterval: REFETCH_MS,
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<AlertsResponse | ReadonlyArray<AlertItem>>(
        '/monitor/ranges/{rangeId}/alerts',
        { method: 'GET', pathParams: { rangeId: rangeId ?? '' }, signal },
      );
      if (Array.isArray(resp)) return resp;
      return (resp as AlertsResponse).items ?? [];
    },
  });
}

/** Alerts across every range the caller's tenant owns, newest-first. */
export function useTenantAlerts() {
  return useQuery<ReadonlyArray<AlertItem>>({
    queryKey: ['alerts', 'tenant'],
    refetchInterval: REFETCH_MS,
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<AlertsResponse | ReadonlyArray<AlertItem>>(
        '/monitor/tenant/alerts',
        { method: 'GET', signal },
      );
      if (Array.isArray(resp)) return resp;
      return (resp as AlertsResponse).items ?? [];
    },
  });
}
